import { formatCPF, formatPhone } from '@westhetics/ui'
import { fieldErrors } from '@/lib/errors'
import type {
  Audience,
  ClinicAddress,
  ClinicProfileInput,
  ClinicRecord,
  ClinicSignatureMode,
  PersonDocument,
} from './api'
import {
  docTypeOf,
  formatDocNumber,
  isValidCNPJ,
  isValidCPF,
  normalizeDocNumber,
  onlyDigits,
} from './doc-number'
import { DEFAULT_TIMEZONE, TIMEZONES } from './labels'
import type { FormErrors } from './use-form-state'

/**
 * O formulário do cadastro da clínica, comum a Empresa, à implantação e ao painel root
 * (RN-ADM-01 a 08). Os campos ficam como a pessoa digita (com máscara); a forma do contrato sai dos
 * montadores no fim do arquivo. As mensagens são as da API, para o erro dizer o mesmo nos dois lados.
 */

export interface ClinicFormState {
  docNumber: string
  legalName: string
  tradeName: string
  postalCode: string
  street: string
  number: string
  complement: string
  district: string
  city: string
  state: string
  legalEmail: string
  phone: string
  timezone: string
  representativeName: string
  representativeDocNumber: string
  witness1Name: string
  witness1DocNumber: string
  witness2Name: string
  witness2DocNumber: string
  clinicSignatureMode: ClinicSignatureMode
  audiences: Audience[]
}

export type ClinicFormKey = keyof ClinicFormState

export const EMPTY_CLINIC_FORM: ClinicFormState = {
  docNumber: '',
  legalName: '',
  tradeName: '',
  postalCode: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  legalEmail: '',
  phone: '',
  timezone: DEFAULT_TIMEZONE,
  representativeName: '',
  representativeDocNumber: '',
  witness1Name: '',
  witness1DocNumber: '',
  witness2Name: '',
  witness2DocNumber: '',
  clinicSignatureMode: 'manual',
  audiences: ['patient'],
}

// Os campos de cada bloco: é por eles que cada tela diz o que confere ao salvar.
export const DOC_NUMBER_KEYS = ['docNumber'] as const satisfies readonly ClinicFormKey[]
export const IDENTIFICATION_KEYS = [
  'legalName',
  'tradeName',
] as const satisfies readonly ClinicFormKey[]
export const ADDRESS_KEYS = [
  'postalCode',
  'street',
  'number',
  'complement',
  'district',
  'city',
  'state',
] as const satisfies readonly ClinicFormKey[]
export const CONTACT_KEYS = [
  'legalEmail',
  'phone',
  'timezone',
] as const satisfies readonly ClinicFormKey[]
export const REPRESENTATIVE_KEYS = [
  'representativeName',
  'representativeDocNumber',
] as const satisfies readonly ClinicFormKey[]
export const WITNESS_KEYS = [
  'witness1Name',
  'witness1DocNumber',
  'witness2Name',
  'witness2DocNumber',
] as const satisfies readonly ClinicFormKey[]

/** "01310100" → "01310-100" (aceita entrada parcial durante a digitação). */
export function formatPostalCode(text: string) {
  const digits = onlyDigits(text).slice(0, 8)
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits
}

/** O telefone guardado (E.164) como a pessoa o lê: sem o +55 do Brasil; de outro país, com o DDI. */
export function phoneToInput(phone: string | null) {
  if (!phone) return ''
  const digits = onlyDigits(phone)
  const isBrazilian = digits.startsWith('55') && (digits.length === 12 || digits.length === 13)
  return isBrazilian ? formatPhone(digits.slice(2)) : `+${digits}`
}

/** A máscara durante a digitação: DDD e número; quem começa por "+" digita o DDI à mão. */
export function maskPhone(text: string) {
  const digits = onlyDigits(text)
  return text.trim().startsWith('+') ? `+${digits.slice(0, 15)}` : formatPhone(digits.slice(0, 11))
}

/** O que foi digitado em E.164, com +55 quando não há DDI (RT-17). `null` quando não fecha. */
export function phoneToE164(text: string): string | null {
  const digits = onlyDigits(text)
  if (text.trim().startsWith('+')) return /^[1-9]\d{7,14}$/.test(digits) ? `+${digits}` : null
  return digits.length === 10 || digits.length === 11 ? `+55${digits}` : null
}

export function clinicFormFrom(clinic: ClinicRecord): ClinicFormState {
  const [first, second] = clinic.witnesses
  // A clínica de cadastro próprio nasce sem endereço (RN-ADM-89): os campos começam vazios.
  const address = clinic.address
  return {
    docNumber: formatDocNumber(clinic.docNumber, clinic.docType),
    legalName: clinic.legalName,
    tradeName: clinic.tradeName ?? '',
    postalCode: address ? formatPostalCode(address.postalCode) : '',
    street: address?.street ?? '',
    number: address?.number ?? '',
    complement: address?.complement ?? '',
    district: address?.district ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    legalEmail: clinic.legalEmail,
    phone: phoneToInput(clinic.phone),
    timezone: clinic.timezone,
    representativeName: clinic.legalRepresentative?.name ?? '',
    representativeDocNumber: formatCPF(clinic.legalRepresentative?.docNumber ?? ''),
    witness1Name: first?.name ?? '',
    witness1DocNumber: formatCPF(first?.docNumber ?? ''),
    witness2Name: second?.name ?? '',
    witness2DocNumber: formatCPF(second?.docNumber ?? ''),
    clinicSignatureMode: clinic.clinicSignatureMode,
    audiences: clinic.audiences,
  }
}

// Validação -------------------------------------------------------------------------------------

const MSG_EMAIL = 'Confira o e-mail: precisa ter @ e um domínio, como nome@clinica.com.br.'

export function personNameError(name: string): string | undefined {
  const text = name.trim()
  if (text.length === 0) return 'Informe o nome completo.'
  if (text.length < 2) return 'O nome precisa de pelo menos 2 letras.'
  if (text.length > 120) return 'O nome pode ter no máximo 120 caracteres.'
  if (/\d/.test(text)) return 'O nome não pode ter números.'
  return undefined
}

export function emailError(email: string): string | undefined {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? undefined : MSG_EMAIL
}

function cpfError(text: string): string | undefined {
  const digits = onlyDigits(text)
  if (digits.length !== 11) return 'O CPF precisa de 11 dígitos.'
  if (!isValidCPF(digits)) return 'Esse CPF não é válido. Confira os dois últimos dígitos.'
  return undefined
}

export function docNumberError(text: string): string | undefined {
  const doc = normalizeDocNumber(text)
  if (doc.length === 0) return 'Informe o CNPJ, ou o CPF se a clínica é pessoa física.'
  if (doc.length === 14) {
    return isValidCNPJ(doc) ? undefined : 'Esse CNPJ não é válido. Confira os dois últimos dígitos.'
  }
  if (/^\d{11}$/.test(doc)) {
    return isValidCPF(doc) ? undefined : 'Esse CPF não é válido. Confira os dois últimos dígitos.'
  }
  return 'O CNPJ tem 14 caracteres e o CPF tem 11. Confira o número.'
}

const WITNESS_ORDINALS = ['primeira', 'segunda'] as const

/** As testemunhas preenchidas, na ordem em que vão impressas (RN-ADM-06). */
function witnessSlots(values: ClinicFormState) {
  return (
    [
      { nameKey: 'witness1Name', docKey: 'witness1DocNumber' },
      { nameKey: 'witness2Name', docKey: 'witness2DocNumber' },
    ] as const
  ).map((slot, index) => ({
    ...slot,
    ordinal: WITNESS_ORDINALS[index]!,
    name: values[slot.nameKey].trim(),
    docNumber: onlyDigits(values[slot.docKey]),
  }))
}

/** A clínica não tem nenhuma testemunha digitada: a etapa segue sem elas. */
export function hasNoWitness(values: ClinicFormState) {
  return witnessSlots(values).every((slot) => slot.name === '' && slot.docNumber === '')
}

export function validateClinicForm(values: ClinicFormState): FormErrors<ClinicFormState> {
  const errors: FormErrors<ClinicFormState> = {}
  const set = (key: ClinicFormKey, message: string | undefined) => {
    if (message && !errors[key]) errors[key] = message
  }

  set('docNumber', docNumberError(values.docNumber))

  const legalName = values.legalName.trim()
  if (legalName.length < 2 || legalName.length > 120) {
    set('legalName', 'Informe a razão social como está no CNPJ.')
  }
  const tradeName = values.tradeName.trim()
  if (tradeName !== '' && (tradeName.length < 2 || tradeName.length > 80)) {
    set('tradeName', 'O nome fantasia tem de 2 a 80 caracteres.')
  }

  if (onlyDigits(values.postalCode).length !== 8) set('postalCode', 'O CEP precisa de 8 dígitos.')
  if (values.street.trim().length < 2) set('street', 'Informe a rua.')
  if (values.number.trim().length < 1) set('number', 'Informe o número.')
  if (values.complement.trim().length > 60) {
    set('complement', 'O complemento tem até 60 caracteres.')
  }
  if (values.district.trim().length < 2) set('district', 'Informe o bairro.')
  if (values.city.trim().length < 2) set('city', 'Informe a cidade.')
  if (values.state.trim().length !== 2) set('state', 'Informe a UF.')

  set('legalEmail', emailError(values.legalEmail))
  if (values.phone.trim() !== '' && !phoneToE164(values.phone)) {
    set('phone', 'O telefone precisa de DDD e número, como 11 91234-5678.')
  }
  if (!TIMEZONES.some((timezone) => timezone.value === values.timezone)) {
    set('timezone', 'Escolha um fuso horário do Brasil.')
  }

  // Responsável legal: opcional, mas nome e CPF vêm juntos (RN-ADM-08).
  const representativeName = values.representativeName.trim()
  const representativeDoc = onlyDigits(values.representativeDocNumber)
  if (representativeName !== '' || representativeDoc !== '') {
    set('representativeName', personNameError(representativeName))
    set(
      'representativeDocNumber',
      representativeDoc === '' ? 'Informe o CPF.' : cpfError(representativeDoc),
    )
  }

  // Testemunhas: de 0 a 2, nome e CPF juntos, diferentes entre si e da responsável legal (RN-ADM-06).
  const slots = witnessSlots(values)
  for (const slot of slots) {
    if (slot.name === '' && slot.docNumber === '') continue
    set(
      slot.nameKey,
      slot.name === ''
        ? `Informe o nome da ${slot.ordinal} testemunha.`
        : personNameError(slot.name),
    )
    set(
      slot.docKey,
      slot.docNumber === ''
        ? `Informe também o CPF da ${slot.ordinal} testemunha.`
        : cpfError(slot.docNumber),
    )
    if (representativeDoc !== '' && slot.docNumber === representativeDoc) {
      set(
        slot.docKey,
        `A ${slot.ordinal} testemunha não pode ser a responsável legal. Escolha outra pessoa.`,
      )
    }
  }
  const [first, second] = slots
  if (first && second && first.docNumber !== '' && first.docNumber === second.docNumber) {
    set('witness2DocNumber', 'As duas testemunhas precisam ser pessoas diferentes.')
  }

  return errors
}

// Do formulário para o contrato -----------------------------------------------------------------

function addressOf(values: ClinicFormState): ClinicAddress {
  return {
    postalCode: onlyDigits(values.postalCode),
    street: values.street.trim(),
    number: values.number.trim(),
    complement: values.complement.trim() || null,
    district: values.district.trim(),
    city: values.city.trim(),
    state: values.state.trim().toUpperCase(),
  }
}

/** Identificação, endereço e contato: o que Empresa, a implantação e o painel root salvam igual. */
export function companyInput(values: ClinicFormState) {
  return {
    legalName: values.legalName.trim(),
    tradeName: values.tradeName.trim() || null,
    address: addressOf(values),
    phone: phoneToE164(values.phone),
    legalEmail: values.legalEmail.trim().toLowerCase(),
    timezone: values.timezone,
  } satisfies ClinicProfileInput
}

/** O documento, só quando a gestora pode corrigi-lo (RN-ADM-88); o tipo sai do tamanho. */
export function documentInput(values: ClinicFormState) {
  const docNumber = normalizeDocNumber(values.docNumber)
  return { docNumber, docType: docTypeOf(docNumber) } satisfies ClinicProfileInput
}

export function representativeInput(values: ClinicFormState) {
  const name = values.representativeName.trim()
  const legalRepresentative: PersonDocument | null =
    name === ''
      ? null
      : { name, docType: 'cpf', docNumber: onlyDigits(values.representativeDocNumber) }
  return { legalRepresentative } satisfies ClinicProfileInput
}

export function witnessesInput(values: ClinicFormState) {
  const witnesses: PersonDocument[] = witnessSlots(values)
    .filter((slot) => slot.name !== '')
    .map((slot) => ({ name: slot.name, docType: 'cpf', docNumber: slot.docNumber }))
  return { witnesses, clinicSignatureMode: values.clinicSignatureMode } satisfies ClinicProfileInput
}

// Do 422 para o formulário ----------------------------------------------------------------------

const FIELD_KEYS: Record<string, ClinicFormKey> = {
  docNumber: 'docNumber',
  docType: 'docNumber',
  legalName: 'legalName',
  tradeName: 'tradeName',
  address: 'postalCode',
  'address.postalCode': 'postalCode',
  'address.street': 'street',
  'address.number': 'number',
  'address.complement': 'complement',
  'address.district': 'district',
  'address.city': 'city',
  'address.state': 'state',
  phone: 'phone',
  legalEmail: 'legalEmail',
  timezone: 'timezone',
  legalRepresentative: 'representativeName',
  'legalRepresentative.name': 'representativeName',
  'legalRepresentative.docNumber': 'representativeDocNumber',
  'legalRepresentative.docType': 'representativeDocNumber',
  witnesses: 'witness1Name',
  clinicSignatureMode: 'clinicSignatureMode',
  audiences: 'audiences',
}

/** O campo do formulário que corresponde a um caminho do 422 (`address.postalCode`, `witnesses.1.docNumber`). */
export function clinicFieldKey(path: string, values: ClinicFormState): ClinicFormKey | undefined {
  const witness = /^witnesses\.(\d)\.(name|docNumber|docType)$/.exec(path)
  if (witness) {
    // O índice do contrato é o da lista enviada, que só leva as testemunhas preenchidas.
    const sent = witnessSlots(values).filter((slot) => slot.name !== '')
    const slot = sent[Number(witness[1])]
    if (!slot) return undefined
    return witness[2] === 'name' ? slot.nameKey : slot.docKey
  }
  return FIELD_KEYS[path]
}

/**
 * Os erros por campo de um 422, na chave do formulário. O que não tem campo na tela volta em
 * `unplaced`, para o `ActionError` dizer mesmo assim.
 */
export function placeFieldErrors<T>(
  error: unknown,
  keyOf: (path: string) => keyof T | undefined,
): { fields: FormErrors<T>; unplaced: string[] } {
  const fields: FormErrors<T> = {}
  const unplaced: string[] = []
  for (const [path, message] of Object.entries(fieldErrors(error))) {
    const key = keyOf(path)
    if (key === undefined) unplaced.push(message)
    else if (!fields[key]) fields[key] = message
  }
  return { fields, unplaced }
}
